import { useEffect, useState } from 'react';
import {
    Grid2,
    TextField,
    MenuItem,
    Tooltip,
    IconButton,
    Stack,
    Box,
    Checkbox,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    Tabs,
    Tab,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useQueryClient } from '@tanstack/react-query';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import { useTranslation } from 'react-i18next';
import {
    useGetCommissionGroup,
    useAddCommissionGroup,
    useEditCommissionGroup,
    getGetCommissionGroupsQueryKey,
    createCommissionGroupStripeCustomer,
} from '@openapi/commission-groups/commission-groups';
import { useGetShow } from '@openapi/shows/shows';
import { useGetPaymentMethods } from '@openapi/payment-methods/payment-methods';
import CommissionMatrix from './components/CommissionMatrix';
import MigratePaymentTypePanel from './components/MigratePaymentTypePanel';
import CommissionGroupMembers from './components/CommissionGroupMembers';
import { useAuth } from '../../hooks/useAuth';
import { PAYMENT_METHOD_TYPES, SIMPLE_EMAIL_REGEX } from '../../helpers/constants';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// Billing types that actually bill a card/bank the partner saved with us. The
// others (a/r, a/p) settle by invoice, so the Stripe block is noise for them.
const PARTNER_CARD_BILLING_TYPES = ['card', 'transfer', 'deposit'];

const BILLING_TYPE_OPTIONS = [
    { value: 'ar', labelKey: 'commissionGroups.billingType.options.ar' },
    { value: 'ap', labelKey: 'commissionGroups.billingType.options.ap' },
    { value: 'deposit', labelKey: 'commissionGroups.billingType.options.deposit' },
    { value: 'card', labelKey: 'commissionGroups.billingType.options.card' },
    { value: 'transfer', labelKey: 'commissionGroups.billingType.options.transfer' },
];

// Setup checklist per billing type. Billing type is purely an indicator, so
// these guide the admin on what to configure for each billing model. `key`
// names the copy under commissionGroups.checklists.<type>.*; `check` reads the
// current config and reports whether that step is done. An item with no `check`
// is informational and always renders as satisfied.
const BILLING_TYPE_CHECKLISTS = {
    ar: [
        { key: 'onHouseOnly', check: ({ links }) => links.length > 0 },
        {
            key: 'markAgentCollected',
            check: ({ links }) => links.length > 0 && links.every((l) => l.agentCollected),
        },
        { key: 'invoice' },
    ],
    ap: [
        { key: 'venueCollected', check: ({ links }) => links.length > 0 },
        {
            key: 'notAgentCollected',
            check: ({ links }) => links.length > 0 && links.every((l) => !l.agentCollected),
        },
        { key: 'invoice' },
    ],
    deposit: [
        { key: 'createCardMethod' },
        { key: 'limitToThatMethod', check: ({ links }) => links.length === 1 },
        { key: 'platformFee' },
        { key: 'connectReceives' },
    ],
    card: [
        {
            key: 'onHouseOnly',
            check: ({ links }) => links.length > 0 && links.every((l) => l.agentCollected),
        },
        { key: 'setupCustomer', check: ({ stripeCustomerId }) => !!stripeCustomerId },
        { key: 'partnerAddsCard' },
        { key: 'autoBill' },
        { key: 'pastEventsLocked' },
    ],
    transfer: [
        {
            key: 'sameAsCard',
            check: ({ links, stripeCustomerId }) =>
                links.length > 0 && links.every((l) => l.agentCollected) && !!stripeCustomerId,
        },
        { key: 'partnerAddsBank' },
        { key: 'async' },
    ],
};

function BillingTypeChecklist({ t, billingType, paymentMethodLinks, stripeCustomerId }) {
    const checklist = BILLING_TYPE_CHECKLISTS[billingType];
    if (!checklist) return null;

    const ctx = { links: paymentMethodLinks, stripeCustomerId };

    return (
        <BlockWithTitle title={t(`commissionGroups.checklists.${billingType}.title`)}>
            {checklist.map((item) => {
                const checked = item.check ? item.check(ctx) : true;
                const Icon = checked ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
                return (
                    <Box
                        key={item.key}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.5,
                            mb: 0.25,
                            textAlign: 'left',
                        }}
                    >
                        <Icon
                            sx={{
                                fontSize: 16,
                                mt: 0.25,
                                color: checked ? 'success.main' : 'text.disabled',
                            }}
                        />
                        <Typography
                            variant="body2"
                            color={checked ? 'text.primary' : 'text.secondary'}
                        >
                            {t(`commissionGroups.checklists.${billingType}.${item.key}`)}
                        </Typography>
                    </Box>
                );
            })}
        </BlockWithTitle>
    );
}

const BILLING_TYPE_VALUES = BILLING_TYPE_OPTIONS.map((option) => option.value);

function EditCommissionGroupPage({ newCommissionGroup }) {
    const { t } = useTranslation();
    const { showId, commissionGroupId } = useParams();
    const { showModal } = useModals();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { notifyError, notifySuccess } = useNotify();
    const { user } = useAuth();
    const [commissionMatrix, setCommissionMatrix] = useState({});

    const isExisting = !newCommissionGroup;
    const [paymentMethodLinks, setPaymentMethodLinks] = useState([]);
    // Settings (save-deferred form) and Affiliates (instant actions) are
    // separate tabs so the Save button unambiguously covers everything on
    // its tab, and the daily task - managing affiliates - loads without
    // scrolling past configuration. New groups have no members yet, so they
    // get the settings form only.
    const [activeTab, setActiveTab] = useState(0);

    const { data: showData } = useGetShow(Number(showId), {
        query: { onError: notifyError },
    });
    const venueId = showData?.venueId;

    const { data: venuePaymentMethodsData } = useGetPaymentMethods(
        Number(venueId),
        {},
        { query: { enabled: !!venueId, onError: notifyError } },
    );
    const venuePaymentMethods = venuePaymentMethodsData?.paymentMethods ?? [];

    // Billing type is only an indicator, so it can't be the sole gate for the
    // partner-card block: a group linked to a net_rate_company method has its
    // company card auto-billed (NetRateService requires the Stripe customer)
    // whatever the billing-type label says, and this page holds the only
    // control that creates that customer.
    const hasNetRateCompanyLink = paymentMethodLinks.some(
        (l) =>
            venuePaymentMethods.find((pm) => pm.id === l.paymentMethodId)?.methodType ===
            PAYMENT_METHOD_TYPES.NET_RATE_COMPANY,
    );

    const {
        data: groupData,
        isLoading,
        error,
    } = useGetCommissionGroup(Number(commissionGroupId), {
        query: {
            enabled: isExisting && Boolean(commissionGroupId),
            onError: (err) => {
                notifyError(err);
                navigate(`/shows/${showId}/commissiongroups`);
            },
        },
    });

    useEffect(() => {
        if (error) {
            navigate(`/shows/${showId}/commissiongroups`);
        }
    }, [error, navigate, showId]);

    useEffect(() => {
        if (groupData?.commissionMatrix) {
            setCommissionMatrix(groupData.commissionMatrix);
        }
        if (groupData?.paymentMethods) {
            setPaymentMethodLinks(groupData.paymentMethods);
        }
    }, [groupData]);

    const addMutation = useAddCommissionGroup();
    const editMutation = useEditCommissionGroup();

    const onSubmit = async (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    const payload = {
                        name: values.name,
                        commissionMatrix,
                        affiliateContactEmail: values.affiliateContactEmail || undefined,
                        billingType: values.billingType || 'ar',
                        dynamicPricingEnabled: !!values.dynamicPricingEnabled,
                        paymentMethods: paymentMethodLinks,
                    };
                    if (isExisting) {
                        await editMutation.mutateAsync({
                            id: Number(commissionGroupId),
                            data: payload,
                        });
                        notifySuccess(t('commissionGroups.notifications.updated'));
                    } else {
                        await addMutation.mutateAsync({
                            showId: Number(showId),
                            data: payload,
                        });
                        notifySuccess(t('commissionGroups.notifications.added'));
                    }
                    await queryClient.invalidateQueries({
                        queryKey: getGetCommissionGroupsQueryKey(Number(showId)),
                    });
                    navigate(`/shows/${showId}/commissiongroups`);
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    if (isExisting && isLoading) {
        return true;
    }

    const validateSchema = Yup.object().shape({
        name: Yup.string().required(t('common.required')),
        billingType: Yup.string()
            .oneOf(BILLING_TYPE_VALUES, t('common.required'))
            .required(t('common.required')),
        affiliateContactEmail: Yup.string()
            .transform((v) => (v === '' ? null : v))
            .nullable()
            .test('comma-separated-email-list', t('errors.email'), (value) => {
                if (!value || !value.trim()) return true;
                const emails = value
                    .split(',')
                    .map((e) => e.trim())
                    .filter(Boolean);
                return emails.length > 0 && emails.every((email) => SIMPLE_EMAIL_REGEX.test(email));
            }),
    });

    const initialValues = isExisting
        ? {
              name: groupData?.name ?? '',
              billingType: groupData?.billingType ?? 'ar',
              affiliateContactEmail: groupData?.affiliateContactEmail ?? '',
              dynamicPricingEnabled: groupData?.dynamicPricingEnabled ?? false,
              stripeCustomerId: groupData?.stripeCustomerId ?? '',
              setupPaymentHash: groupData?.setupPaymentHash ?? '',
          }
        : {
              name: '',
              billingType: 'ar',
              affiliateContactEmail: '',
              dynamicPricingEnabled: false,
              stripeCustomerId: '',
              setupPaymentHash: '',
          };

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validateSchema}
            enableReinitialize
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle
                        title={
                            newCommissionGroup
                                ? t('commissionGroups.newCommissionGroupTitle')
                                : (groupData?.name ?? '')
                        }
                    >
                        {isExisting && (
                            <Tabs
                                value={activeTab}
                                onChange={(_, v) => setActiveTab(v)}
                                sx={{ mb: 2, width: '100%' }}
                            >
                                <Tab label={t('commissionGroups.tabs.settings')} />
                                <Tab label={t('commissionGroups.tabs.affiliates')} />
                            </Tabs>
                        )}
                        {activeTab === 1 && isExisting ? (
                            <Box sx={{ width: '100%' }}>
                                <CommissionGroupMembers
                                    commissionGroupId={Number(commissionGroupId)}
                                />
                            </Box>
                        ) : (
                            <Grid2 container spacing={3}>
                                <Grid2 size={{ xs: 12, md: 8 }} sx={{ order: { xs: 2, md: 1 } }}>
                                    <BlockWithTitle title={t('commissionGroups.model')}>
                                        <Grid2 size={{ xs: 12 }}>
                                            <TextField
                                                fullWidth
                                                label={t('commissionGroups.columns.name')}
                                                value={values.name}
                                                onChange={(ev) =>
                                                    setFieldValue('name', ev.target.value)
                                                }
                                                error={touched.name && Boolean(errors.name)}
                                                helperText={touched.name ? errors.name : ''}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                alignItems="center"
                                                sx={{ mb: 1 }}
                                            >
                                                <Box sx={{ fontSize: 14 }}>
                                                    {t('commissionGroups.columns.billingType')}
                                                </Box>
                                                <Tooltip
                                                    title={
                                                        <Stack sx={{ maxWidth: 560, py: 0.5 }}>
                                                            {BILLING_TYPE_OPTIONS.map((option) => (
                                                                <Box
                                                                    key={option.value}
                                                                    sx={{ mb: 0.75 }}
                                                                >
                                                                    <strong>
                                                                        {t(option.labelKey)}:
                                                                    </strong>{' '}
                                                                    {t(
                                                                        `commissionGroups.billingType.descriptions.${option.value}`,
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.25 }}
                                                        aria-label={t(
                                                            'commissionGroups.billingType.infoAriaLabel',
                                                        )}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                            <TextField
                                                select
                                                fullWidth
                                                value={values.billingType}
                                                onChange={(ev) =>
                                                    setFieldValue('billingType', ev.target.value)
                                                }
                                                error={
                                                    touched.billingType &&
                                                    Boolean(errors.billingType)
                                                }
                                                helperText={
                                                    touched.billingType ? errors.billingType : ''
                                                }
                                                slotProps={{
                                                    select: { sx: { textAlign: 'left' } },
                                                }}
                                            >
                                                <MenuItem value="">
                                                    {t('commissionGroups.billingType.placeholder')}
                                                </MenuItem>
                                                {BILLING_TYPE_OPTIONS.map((option) => (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {t(option.labelKey)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                                            <TextField
                                                fullWidth
                                                label={t(
                                                    'commissionGroups.columns.affiliateContactEmail',
                                                )}
                                                placeholder={t(
                                                    'commissionGroups.affiliateContactEmailPlaceholder',
                                                )}
                                                value={values.affiliateContactEmail}
                                                onChange={(ev) =>
                                                    setFieldValue(
                                                        'affiliateContactEmail',
                                                        ev.target.value,
                                                    )
                                                }
                                                error={
                                                    touched.affiliateContactEmail &&
                                                    Boolean(errors.affiliateContactEmail)
                                                }
                                                helperText={
                                                    touched.affiliateContactEmail
                                                        ? errors.affiliateContactEmail
                                                        : t(
                                                              'commissionGroups.affiliateContactEmailHelp',
                                                          )
                                                }
                                            />
                                        </Grid2>
                                        <Grid2
                                            size={{ xs: 12 }}
                                            sx={{ mt: 2 }}
                                            style={{ textAlign: 'left' }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={!!values.dynamicPricingEnabled}
                                                        onChange={(ev) =>
                                                            setFieldValue(
                                                                'dynamicPricingEnabled',
                                                                ev.target.checked,
                                                            )
                                                        }
                                                    />
                                                }
                                                label={t('commissionGroups.dynamicPricing.label')}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('commissionGroups.dynamicPricing.help')}
                                            </Typography>
                                        </Grid2>
                                        {venuePaymentMethods.length > 0 && (
                                            <Grid2
                                                size={{ xs: 12 }}
                                                sx={{ mt: 2 }}
                                                style={{ textAlign: 'left' }}
                                            >
                                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                    {t(
                                                        'commissionGroups.paymentMethods.title',
                                                        'Allowed Payment Methods',
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 1 }}
                                                >
                                                    {t(
                                                        'commissionGroups.paymentMethods.description',
                                                        'Agents in this group can only use selected payment methods. Toggle "Agent Collected" if the agent collects the payment on behalf of the venue.',
                                                    )}
                                                </Typography>
                                                <TableContainer
                                                    component={Paper}
                                                    variant="outlined"
                                                >
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell padding="checkbox" />
                                                                <TableCell>
                                                                    {t(
                                                                        'commissionGroups.paymentMethods.name',
                                                                        'Payment Method',
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {t(
                                                                        'commissionGroups.paymentMethods.agentCollected',
                                                                        'Agent Collected',
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {venuePaymentMethods.map((pm) => {
                                                                const link =
                                                                    paymentMethodLinks.find(
                                                                        (l) =>
                                                                            l.paymentMethodId ===
                                                                            pm.id,
                                                                    );
                                                                const isSelected = !!link;
                                                                return (
                                                                    <TableRow key={pm.id}>
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox
                                                                                checked={isSelected}
                                                                                onChange={(e) => {
                                                                                    if (
                                                                                        e.target
                                                                                            .checked
                                                                                    ) {
                                                                                        setPaymentMethodLinks(
                                                                                            (
                                                                                                prev,
                                                                                            ) => [
                                                                                                ...prev,
                                                                                                {
                                                                                                    paymentMethodId:
                                                                                                        pm.id,
                                                                                                    agentCollected: false,
                                                                                                },
                                                                                            ],
                                                                                        );
                                                                                    } else {
                                                                                        setPaymentMethodLinks(
                                                                                            (
                                                                                                prev,
                                                                                            ) =>
                                                                                                prev.filter(
                                                                                                    (
                                                                                                        l,
                                                                                                    ) =>
                                                                                                        l.paymentMethodId !==
                                                                                                        pm.id,
                                                                                                ),
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {pm.name}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Switch
                                                                                checked={
                                                                                    !!link?.agentCollected
                                                                                }
                                                                                disabled={
                                                                                    !isSelected
                                                                                }
                                                                                onChange={(e) => {
                                                                                    setPaymentMethodLinks(
                                                                                        (prev) =>
                                                                                            prev.map(
                                                                                                (
                                                                                                    l,
                                                                                                ) =>
                                                                                                    l.paymentMethodId ===
                                                                                                    pm.id
                                                                                                        ? {
                                                                                                              ...l,
                                                                                                              agentCollected:
                                                                                                                  e
                                                                                                                      .target
                                                                                                                      .checked,
                                                                                                          }
                                                                                                        : l,
                                                                                            ),
                                                                                    );
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Grid2>
                                        )}
                                        {/* The partner's saved card/bank that the venue
                                        auto-bills for invoices. Shown for the billing
                                        types that bill a partner card, and for groups
                                        linked to a net_rate_company method (their
                                        company card needs this customer regardless of
                                        the billing-type label) - for plain A/R and A/P
                                        it was pure noise. Still shown once a customer
                                        exists, so an already-shared setup link never
                                        becomes unreachable after a type change. */}
                                        {isExisting &&
                                            (PARTNER_CARD_BILLING_TYPES.includes(
                                                values.billingType,
                                            ) ||
                                                hasNetRateCompanyLink ||
                                                !!values?.stripeCustomerId) && (
                                                <Grid2
                                                    size={{ xs: 12 }}
                                                    sx={{ mt: 2 }}
                                                    style={{ textAlign: 'left' }}
                                                >
                                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                        {t('commissionGroups.partnerCard.title')}{' '}
                                                        {values?.stripeCustomerId && (
                                                            <Chip
                                                                style={{ fontSize: 8 }}
                                                                label={`ID: ${values.stripeCustomerId}`}
                                                                size="small"
                                                            />
                                                        )}
                                                    </Typography>
                                                    {values?.stripeCustomerId ? (
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ mb: 0.5 }}
                                                            >
                                                                {t(
                                                                    'commissionGroups.partnerCard.setupLinkHelp',
                                                                )}
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={`${window.location.origin}/setup-payment/${values.stripeCustomerId}/${values.setupPaymentHash}`}
                                                                slotProps={{
                                                                    input: { readOnly: true },
                                                                }}
                                                                onClick={(e) => e.target.select()}
                                                                sx={{ mb: 1 }}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <CommonButton
                                                            label={t(
                                                                'commissionGroups.partnerCard.setupButton',
                                                            )}
                                                            buttonType="secondary"
                                                            onClick={async () => {
                                                                try {
                                                                    const {
                                                                        stripeCustomerId,
                                                                        setupPaymentHash,
                                                                    } =
                                                                        await createCommissionGroupStripeCustomer(
                                                                            commissionGroupId,
                                                                        );

                                                                    // set it on the already loaded group data to avoid refetching immediately
                                                                    setFieldValue(
                                                                        'stripeCustomerId',
                                                                        stripeCustomerId,
                                                                    );
                                                                    setFieldValue(
                                                                        'setupPaymentHash',
                                                                        setupPaymentHash,
                                                                    );

                                                                    notifySuccess(
                                                                        t(
                                                                            'commissionGroups.partnerCard.created',
                                                                        ),
                                                                    );
                                                                } catch (err) {
                                                                    notifyError(err);
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </Grid2>
                                            )}
                                        {isExisting && user?.isSuperAdmin && (
                                            <Grid2
                                                size={{ xs: 12 }}
                                                sx={{ mt: 2 }}
                                                style={{ textAlign: 'left' }}
                                            >
                                                <MigratePaymentTypePanel
                                                    commissionGroupId={commissionGroupId}
                                                    venuePaymentMethods={venuePaymentMethods}
                                                    paymentMethodLinks={paymentMethodLinks}
                                                />
                                            </Grid2>
                                        )}
                                    </BlockWithTitle>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 4 }} sx={{ order: { xs: 1, md: 2 } }}>
                                    {/* Sticky: the checks react to the payment
                                        method toggles further down the form, so
                                        the checklist stays visible while the
                                        admin scrolls to them. */}
                                    <Box sx={{ position: 'sticky', top: 80 }}>
                                        <BillingTypeChecklist
                                            t={t}
                                            billingType={values.billingType}
                                            paymentMethodLinks={paymentMethodLinks}
                                            stripeCustomerId={groupData?.stripeCustomerId}
                                        />
                                    </Box>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }} sx={{ minWidth: 0, order: 3 }}>
                                    <CommissionMatrix
                                        showId={showId}
                                        commissionMatrix={commissionMatrix}
                                        setCommissionMatrix={setCommissionMatrix}
                                    />
                                </Grid2>
                                {/* Save sits below everything it covers - all
                                settings, matrix and sell permissions save in
                                one submit. */}
                                <Grid2 size={{ xs: 12 }} sx={{ order: 4 }}>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <CommonButton
                                            sx={{ minWidth: 180 }}
                                            onClick={() =>
                                                navigate(`/shows/${showId}/commissiongroups`)
                                            }
                                            label={t('common.cancel')}
                                            buttonType="secondary"
                                        />
                                        <CommonButton
                                            sx={{ minWidth: 180 }}
                                            disabled={
                                                isSubmitting ||
                                                addMutation.isPending ||
                                                editMutation.isPending
                                            }
                                            type="submit"
                                            label={t('common.save')}
                                            buttonType="primary"
                                        />
                                    </Stack>
                                </Grid2>
                            </Grid2>
                        )}
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default EditCommissionGroupPage;
